/** Recruitment Network · requirement detail panel. Opens from the requirement anchor card so a
 *  recruiter can read the whole job spec — and copy its application email or JD — without leaving
 *  the graph. Read-only: editing stays on the requirement page, which the footer links to.
 *
 *  Data comes from the funnel payload the network already fetches (same shape RequisitionDetail
 *  reads), so opening the panel costs no extra request. */
import { useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Copy, Mail, X } from "lucide-react";
import { INTERVIEW_LANGUAGES } from "@talenthop/shared";
import type { InterviewLanguageCode } from "@talenthop/shared";
import { MarkdownView } from "../../../components/MarkdownView.js";
import { displayTitle } from "../displayTitle.js";
import { getRequisitionStatus } from "../requisitionFormat.js";
import type { NetRequisition } from "./networkModel.js";

/** The requisition slice of GET /requisitions/{id}/funnel this panel reads. */
export interface FunnelRequisitionLite {
  jobDescription?: string | null;
  jobCode?: string | null;
  jobEmail?: string | null;
  verticalName?: string;
  industryKey?: string | null;
  config?: Record<string, unknown>;
}

/** A copy button that only claims "Copied" when the clipboard write actually succeeded. */
function CopyButton({ text, label }: { text: string; label: string }): ReactNode {
  const [state, setState] = useState<"idle" | "done" | "failed">("idle");
  return (
    <button
      type="button"
      className={`rn-copy-btn${state === "done" ? " is-done" : ""}`}
      data-tip={state === "failed" ? "Your browser blocked the copy — select the text and copy it manually" : `Copy ${label}`}
      aria-label={`Copy ${label}`}
      onClick={() => {
        navigator.clipboard
          .writeText(text)
          .then(() => {
            setState("done");
            setTimeout(() => setState("idle"), 2000);
          })
          .catch(() => setState("failed"));
      }}
    >
      {state === "done" ? <Check size={14} /> : <Copy size={14} />}
      <span>{state === "done" ? "Copied" : state === "failed" ? "Blocked" : "Copy"}</span>
    </button>
  );
}

const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "");
const list = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && !!x.trim()) : []);

export function RequisitionPanel({
  req,
  detail,
  loading,
  onClose,
}: {
  req: NetRequisition;
  detail: FunnelRequisitionLite | undefined;
  loading: boolean;
  onClose: () => void;
}): ReactNode {
  const title = displayTitle(req.title);
  const status = getRequisitionStatus(req.state);
  const cfg = { ...(req.config as unknown as Record<string, unknown>), ...(detail?.config ?? {}) };
  const jobCode = detail?.jobCode ?? req.jobCode ?? null;
  const jobEmail = detail?.jobEmail ?? null;
  const jd = (detail?.jobDescription ?? "").trim() || str(cfg.summary);

  const experience = str(cfg.experience) || list(cfg.levels).join(" + ");
  const budget = [str(cfg.budgetMin), str(cfg.budgetMax)].filter(Boolean).join(" – ");
  const location = [str(cfg.location), str(cfg.mode) && `(${str(cfg.mode)})`].filter(Boolean).join(" ");
  const facts: [string, string][] = (
    [
      ["Department", str(cfg.department)],
      ["Industry", str(cfg.industry) || (detail?.industryKey ?? "")],
      ["Experience", experience],
      ["Budget", budget],
      ["Location & mode", location],
      ["Notice period", str(cfg.noticePeriod)],
      ["Visa", str(cfg.visa)],
      ["Assessment vertical", detail?.verticalName ?? ""],
    ] as [string, string][]
  ).filter(([, v]) => v);
  const required = list(cfg.requiredSkills).length ? list(cfg.requiredSkills) : list(cfg.skills);
  const bonus = list(cfg.bonusSkills);
  const langs = list(cfg.interviewLanguages).map((code) => {
    const l = INTERVIEW_LANGUAGES[code as InterviewLanguageCode];
    return l ? l.label : code;
  });

  // Everything in one paste — for a job board, a WhatsApp message or an email to a vendor.
  const allText = [
    title,
    jobCode ? `Job code: ${jobCode}` : "",
    jobEmail ? `Apply by email: ${jobEmail}` : "",
    ...facts.map(([k, v]) => `${k}: ${v}`),
    required.length ? `Required skills: ${required.join(", ")}` : "",
    bonus.length ? `Bonus skills: ${bonus.join(", ")}` : "",
    langs.length ? `Interview languages: ${langs.join(", ")}` : "",
    jd ? `\nJob description:\n${jd}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <aside className="rn-detail" aria-label={`${title} — requirement details`}>
      <header className="rn-detail-head">
        <div className="rn-detail-name">
          <strong title={title}>{title}</strong>
          <span>
            {jobCode ?? `REQ-${req.id.slice(0, 6).toUpperCase()}`}
            {str(cfg.location) ? ` · ${str(cfg.location)}` : ""}
          </span>
          <div className="rn-pills">
            <span className={`rn-status s-${status.toLowerCase()}`}>{status}</span>
          </div>
        </div>
        <button type="button" className="rn-icon-btn" onClick={onClose} aria-label="Close requirement details">
          <X size={16} />
        </button>
      </header>

      <div className="rn-detail-body">
        <section>
          <h4>Application email</h4>
          {jobEmail ? (
            <>
              <div className="rn-copy-row">
                <Mail size={14} />
                <code>{jobEmail}</code>
                <CopyButton text={jobEmail} label="application email" />
              </div>
              <p className="rn-muted sm">Applications forwarded here are added to this requirement automatically.</p>
            </>
          ) : (
            <p className="rn-muted">{loading ? "Loading…" : "No email address assigned to this requirement."}</p>
          )}
        </section>

        {facts.length > 0 && (
          <section className="rn-facts">
            {facts.map(([k, v]) => (
              <div key={k}><span>{k}</span><b>{v}</b></div>
            ))}
          </section>
        )}

        {required.length > 0 && (
          <section>
            <h4>Required skills ({required.length})</h4>
            <div className="rn-chips">{required.map((s) => <span key={s} className="rn-chip on">{s}</span>)}</div>
          </section>
        )}
        {bonus.length > 0 && (
          <section>
            <h4>Bonus skills</h4>
            <div className="rn-chips">{bonus.map((s) => <span key={s} className="rn-chip">{s}</span>)}</div>
          </section>
        )}
        {langs.length > 0 && (
          <section>
            <h4>Interview languages</h4>
            <div className="rn-chips">{langs.map((s) => <span key={s} className="rn-chip">{s}</span>)}</div>
          </section>
        )}

        <section>
          <div className="rn-section-head">
            <h4>Job description</h4>
            {jd && <CopyButton text={jd} label="job description" />}
          </div>
          {jd ? (
            <div className="rn-jd"><MarkdownView content={jd} /></div>
          ) : (
            <p className="rn-muted">{loading ? "Loading…" : "No job description on this requirement yet."}</p>
          )}
        </section>
      </div>

      <footer className="rn-detail-foot rn-detail-foot-split">
        <CopyButton text={allText} label="all details" />
        <Link className="rn-cta" to={`/app/requisitions/${req.id}`}>
          Open requirement <ArrowRight size={15} />
        </Link>
      </footer>
    </aside>
  );
}
